import camelCaseKeys from 'camelcase-keys';
import snakeCaseKeys from 'snakecase-keys';

// map-obj (used by camelcase-keys) treats these as leaves; mirror that so behavior matches.
const isTraversable = (value: unknown): value is Record<string, any> | any[] =>
  typeof value === 'object' &&
  value !== null &&
  !(value instanceof RegExp) &&
  !(value instanceof Error) &&
  !(value instanceof Date);

/**
 * Shape of a payload passed to {@link mapKeysDeepToCamelCase}, captured when the
 * transform overflows the call stack. `camelcase-keys`' deep mode recurses once
 * per nesting level, so deeply nested or cyclic payloads throw `RangeError:
 * Maximum call stack size exceeded` — but only on iOS/Safari (JavaScriptCore),
 * whose stack is far smaller than V8's. These metrics tell us which of the two
 * (depth or a cycle) caused a given crash.
 */
export interface DeepTransformDiagnostics {
  /** Deepest nesting level reached (root = 0). */
  maxDepth: number;
  /** Number of containers (objects/arrays) in the payload. */
  nodeCount: number;
  /** A container was reached more than once — a cycle or a shared reference. */
  hadCycle: boolean;
}

type DeepTransformReporter = (message: string, diagnostics: DeepTransformDiagnostics) => void;

let reportDeepTransform: DeepTransformReporter | undefined;

/**
 * Registers (or clears, when called with no argument) the reporter invoked when
 * `mapKeysDeepToCamelCase` overflows the call stack. `libs/utils` deliberately
 * has no Sentry dependency (it sits below `libs/ui`, where the logger lives), so
 * the app wires this up once at bootstrap. It is a no-op until then.
 */
export const setDeepTransformReporter = (fn?: DeepTransformReporter) => {
  reportDeepTransform = fn;
};

/**
 * Iteratively measures a payload's depth, size, and whether it contains a
 * cycle/shared reference. Runs on the heap (explicit stack) and is cycle-safe,
 * so it never overflows even for the pathological payloads that crash the
 * recursive transform. Called only from the crash path, so it costs nothing on
 * the happy path.
 */
const measureDeepPayload = (object: object): DeepTransformDiagnostics => {
  let maxDepth = 0;
  let nodeCount = 0;
  let revisits = 0;
  const seen = new WeakSet<object>();
  const stack: Array<[any, number]> = [[object, 0]];

  for (let entry = stack.pop(); entry !== undefined; entry = stack.pop()) {
    const [node, depth] = entry;
    if (!isTraversable(node)) continue;
    if (seen.has(node)) {
      revisits++;
      continue;
    }
    seen.add(node);
    nodeCount++;
    if (depth > maxDepth) maxDepth = depth;

    if (Array.isArray(node)) {
      for (let i = 0; i < node.length; i++) stack.push([node[i], depth + 1]);
    } else {
      for (const key of Object.keys(node)) stack.push([node[key], depth + 1]);
    }
  }

  return { maxDepth, nodeCount, hadCycle: revisits > 0 };
};

/**
 * Deeply converts object keys to camelCase.
 *
 * `camelcase-keys`' deep mode recurses once per nesting level, so deeply nested
 * or cyclic payloads overflow the JS call stack. iOS/Safari (JavaScriptCore) has
 * a much smaller stack than V8, so those payloads throw `RangeError: Maximum
 * call stack size exceeded` only on iOS. When that happens we capture the
 * payload's shape (see {@link measureDeepPayload}) via the injected reporter,
 * then re-throw the original error unchanged — this adds visibility, it does not
 * change behavior.
 */
export const mapKeysDeepToCamelCase = <T extends Record<string, any> | readonly any[]>(object: T) => {
  try {
    return camelCaseKeys<T, { deep: true }>(object, { deep: true });
  } catch (error) {
    if (reportDeepTransform && error instanceof RangeError && isTraversable(object)) {
      reportDeepTransform('mapKeysDeepToCamelCase overflowed the call stack', measureDeepPayload(object));
    }
    throw error;
  }
};

export const mapKeysDeepToSnakeCase = <T extends Record<string, any> | readonly any[]>(
  object: T,
  options?: { exclude?: ReadonlyArray<string | RegExp> }
) =>
  snakeCaseKeys<T, { deep: true }>(object, {
    deep: true,
    ...(options?.exclude && { exclude: options.exclude }),
  });

export const mapKeysToCamelCase = <T extends Record<string, any> | readonly any[]>(object: T) =>
  camelCaseKeys<T, { deep: false }>(object, { deep: false });

export const mapKeysToSnakeCase = <T extends Record<string, any> | readonly any[]>(object: T) =>
  snakeCaseKeys<T, { deep: false }>(object, { deep: false });

export type { SnakeCaseKeys } from 'snakecase-keys';

type Entries<T> = [keyof T, ValueOf<T>][];
type Keys<T> = Array<keyof T>;
type ValueOf<T> = T[keyof T];

// Same as `Object.entries()` but with type inference
export function objectEntries<T extends object>(obj: T): Entries<T> {
  return Object.entries(obj) as Entries<T>;
}

// Same as `Object.keys()` but with type inference
export function objectKeys<T extends object>(obj: T): Keys<T> {
  return Object.keys(obj) as Keys<T>;
}

// Same as `Object.values()` but with type inference
export function objectValues<T extends object>(obj: T): ValueOf<T>[] {
  return Object.values(obj) as ValueOf<T>[];
}

export const sortBy = <A>(array: A[], by: keyof A, opts: { direction: 'asc' | 'desc'; isDate?: boolean }): A[] => {
  return array?.sort((a, b) => {
    const nA = opts.isDate ? Number(new Date(a[by] as unknown as string)) : a[by];
    const nB = opts.isDate ? Number(new Date(b[by] as unknown as string)) : b[by];

    if (opts.direction === 'desc') {
      if (typeof nA === 'string' && typeof nB === 'string') {
        const sortResult = Intl.Collator().compare(nA, nB);
        return sortResult === 1 ? -1 : 1;
      }

      if (nA > nB) return -1;
      if (nA < nB) return 1;
      return 0;
    }

    if (typeof nA === 'string' && typeof nB === 'string') return Intl.Collator().compare(nA, nB);

    if (nA < nB) return -1;
    if (nA > nB) return 1;
    return 0;
  });
};
